import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import { useSnackbar } from 'notistack';
import dayjs from 'dayjs';
import { useLocation } from 'react-router-dom';
import {
  APIGetLineChatAttentionRequired,
  APIGetLineChatConversations,
} from '../service/api/APILineChat';
import { ILineChatConversation } from '../compiler/lineChat.interface';
import { useAppSelector } from '../hook/store';

interface ILineChatAlertSnapshot {
  unreadConversationCount: number;
  attentionRequiredCount: number;
  staleUnreadCount: number;
  latestUnreadConversation: ILineChatConversation | null;
  lastUpdatedAt: number | null;
}

interface ILineChatAlertContext extends ILineChatAlertSnapshot {
  hasLineChatPermission: boolean;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  browserNotificationSupported: boolean;
  notificationPermission: NotificationPermission | 'unsupported';
  requestBrowserPermission: () => Promise<NotificationPermission | 'unsupported'>;
}

const DEFAULT_SNAPSHOT: ILineChatAlertSnapshot = {
  unreadConversationCount: 0,
  attentionRequiredCount: 0,
  staleUnreadCount: 0,
  latestUnreadConversation: null,
  lastUpdatedAt: null,
};

const LineChatAlertContext = createContext<ILineChatAlertContext | null>(null);

const LINE_CHAT_STALE_MINUTES = 10;
const LINE_CHAT_POLLING_INTERVAL_MS = 20_000;
const LINE_CHAT_PERMISSION_CODE = '05-05';

const isBrowserNotificationSupported = (): boolean =>
  typeof window !== 'undefined' && 'Notification' in window;

export const LineChatAlertProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();
  const { access_token: accessToken, permission: permissionList = [] } = useAppSelector(
    (state) => state.AuthInfo
  );
  const hasLineChatPermission = useMemo(
    () =>
      permissionList.some(
        (permission: any) =>
          permission?.roles_code === LINE_CHAT_PERMISSION_CODE &&
          Number(permission?.read) === 1 &&
          Number(permission?.roles_active ?? 1) !== 0
      ),
    [permissionList]
  );

  const [notificationPermission, setNotificationPermission] = useState<
    NotificationPermission | 'unsupported'
  >(() => (isBrowserNotificationSupported() ? Notification.permission : 'unsupported'));

  const previousSnapshotRef = useRef<IlineChatPreviousSnapshot | null>(null);

  const unreadOverviewQuery = useQuery(
    ['line-chat', 'global-unread-overview'],
    async () => {
      const [unreadResponse, attentionResponse] = await Promise.all([
        APIGetLineChatConversations({
          page: 1,
          limit: 20,
          has_unread: true,
        }),
        APIGetLineChatAttentionRequired({
          page: 1,
          limit: 1,
          reviewed: false,
        }),
      ]);

      const unreadConversations = unreadResponse.data || [];
      const staleUnreadCount = unreadConversations.filter((conversation) => {
        const latestMessageAt = conversation.last_message?.created_at;
        if (!latestMessageAt || conversation.unread_count <= 0) return false;
        return dayjs().diff(dayjs(latestMessageAt), 'minute') >= LINE_CHAT_STALE_MINUTES;
      }).length;

      return {
        unreadConversationCount: unreadResponse.pagination?.total ?? unreadConversations.length,
        attentionRequiredCount:
          attentionResponse.pagination?.total ?? (attentionResponse.data || []).length,
        staleUnreadCount,
        latestUnreadConversation: unreadConversations[0] || null,
        lastUpdatedAt: Date.now(),
      } as ILineChatAlertSnapshot;
    },
    {
      enabled: Boolean(accessToken && hasLineChatPermission),
      refetchInterval: LINE_CHAT_POLLING_INTERVAL_MS,
      refetchOnWindowFocus: true,
      staleTime: 10_000,
      cacheTime: 0,
      retry: 1,
      onError: (error: any) => {
        if (error?.response?.status !== 401) {
          console.warn('Line chat unread polling error:', error);
        }
      },
    }
  );

  const snapshot = hasLineChatPermission ? unreadOverviewQuery.data || DEFAULT_SNAPSHOT : DEFAULT_SNAPSHOT;

  const requestBrowserPermission = useCallback(async () => {
    if (!hasLineChatPermission) {
      enqueueSnackbar('ไม่มีสิทธิ์ใช้งานเมนูไลน์แชท', { variant: 'warning' });
      return notificationPermission;
    }

    if (!isBrowserNotificationSupported()) {
      enqueueSnackbar('เบราว์เซอร์นี้ไม่รองรับการแจ้งเตือน', { variant: 'warning' });
      setNotificationPermission('unsupported');
      return 'unsupported';
    }

    const nextPermission = await Notification.requestPermission();
    setNotificationPermission(nextPermission);

    if (nextPermission === 'granted') {
      enqueueSnackbar('เปิดแจ้งเตือนเบราว์เซอร์สำหรับ Line Chat แล้ว', { variant: 'success' });
    } else {
      enqueueSnackbar('ยังไม่ได้เปิดแจ้งเตือนเบราว์เซอร์', { variant: 'info' });
    }

    return nextPermission;
  }, [enqueueSnackbar, hasLineChatPermission, notificationPermission]);

  useEffect(() => {
    if (!isBrowserNotificationSupported()) return;

    const syncPermission = () => setNotificationPermission(Notification.permission);
    window.addEventListener('focus', syncPermission);
    return () => window.removeEventListener('focus', syncPermission);
  }, []);

  useEffect(() => {
    if (!accessToken || !hasLineChatPermission) {
      previousSnapshotRef.current = null;
      return;
    }

    const previous = previousSnapshotRef.current;
    const latestMessageAt = snapshot.latestUnreadConversation?.last_message?.created_at || '';
    const currentSignature = `${snapshot.unreadConversationCount}:${latestMessageAt}`;

    if (!previous) {
      previousSnapshotRef.current = {
        unreadConversationCount: snapshot.unreadConversationCount,
        signature: currentSignature,
      };
      return;
    }

    const isOnLineChatPage = location.pathname.includes('/customer/line-chat-history');
    const hasUnreadIncrease = snapshot.unreadConversationCount > previous.unreadConversationCount;
    const hasNewLatestMessage =
      snapshot.unreadConversationCount > 0 && currentSignature !== previous.signature;

    if (!isOnLineChatPage && (hasUnreadIncrease || hasNewLatestMessage)) {
      const latestBranchName = snapshot.latestUnreadConversation?.branch_name || 'ไม่ระบุสาขา';
      enqueueSnackbar(
        `มีข้อความ LINE ใหม่ ${snapshot.unreadConversationCount} ห้อง • ล่าสุด: ${latestBranchName}`,
        {
          variant: 'info',
          autoHideDuration: 5000,
        }
      );

      if (
        notificationPermission === 'granted' &&
        typeof document !== 'undefined' &&
        document.visibilityState !== 'visible'
      ) {
        const body = `ตอนนี้มีห้องที่ยังไม่อ่าน ${snapshot.unreadConversationCount} ห้อง (ล่าสุด: ${latestBranchName})`;
        new Notification('LINE แชทใหม่', {
          body,
          tag: 'line-chat-unread-alert',
        });
      }
    }

    previousSnapshotRef.current = {
      unreadConversationCount: snapshot.unreadConversationCount,
      signature: currentSignature,
    };
  }, [
    accessToken,
    enqueueSnackbar,
    hasLineChatPermission,
    location.pathname,
    notificationPermission,
    snapshot.latestUnreadConversation?.branch_name,
    snapshot.latestUnreadConversation?.last_message?.created_at,
    snapshot.unreadConversationCount,
  ]);

  const contextValue = useMemo<ILineChatAlertContext>(
    () => ({
      hasLineChatPermission,
      ...snapshot,
      isLoading: hasLineChatPermission ? unreadOverviewQuery.isLoading : false,
      isError: hasLineChatPermission ? unreadOverviewQuery.isError : false,
      refetch: () => {
        if (!hasLineChatPermission) return;
        unreadOverviewQuery.refetch();
      },
      browserNotificationSupported: hasLineChatPermission && notificationPermission !== 'unsupported',
      notificationPermission,
      requestBrowserPermission,
    }),
    [
      hasLineChatPermission,
      notificationPermission,
      requestBrowserPermission,
      snapshot,
      unreadOverviewQuery.isError,
      unreadOverviewQuery.isLoading,
      unreadOverviewQuery,
    ]
  );

  return (
    <LineChatAlertContext.Provider value={contextValue}>{children}</LineChatAlertContext.Provider>
  );
};

interface IlineChatPreviousSnapshot {
  unreadConversationCount: number;
  signature: string;
}

export const useLineChatAlertContext = (): ILineChatAlertContext => {
  const context = useContext(LineChatAlertContext);
  if (!context) {
    throw new Error('useLineChatAlertContext must be used within LineChatAlertProvider');
  }
  return context;
};
